import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  ClipboardPaste,
  Cloud,
  FileText,
  Image,
  Monitor,
  Plus,
  Square,
  Video,
} from 'lucide-react';
import { cn, ui } from '../../utils';

interface AttachmentMenuProps {
  disabled?: boolean;
  isRecording: boolean;
  onScreenshot: () => void;
  onScreenRecord: () => void;
  onStopRecording: () => void;
  onPhotos: () => void;
  onFiles: () => void;
  onOneDrive: () => void;
  onClipboard: () => void;
}

const MENU_ITEMS = [
  { id: 'screenshot', icon: Monitor, label: 'Screenshot', action: 'onScreenshot', bg: 'bg-blue-500/10', color: 'text-blue-300' },
  { id: 'record', icon: Video, label: 'Screen Record', action: 'onScreenRecord', bg: 'bg-red-500/10', color: 'text-red-300' },
  { id: 'photos', icon: Image, label: 'Photos', action: 'onPhotos', bg: 'bg-emerald-500/10', color: 'text-emerald-300' },
  { id: 'files', icon: FileText, label: 'Files', action: 'onFiles', bg: 'bg-amber-500/10', color: 'text-amber-300' },
  { id: 'onedrive', icon: Cloud, label: 'OneDrive', action: 'onOneDrive', bg: 'bg-blue-500/10', color: 'text-blue-300' },
  { id: 'clipboard', icon: ClipboardPaste, label: 'Clipboard', action: 'onClipboard', bg: 'bg-muted/30', color: 'text-muted-foreground' },
] as const;

export function AttachmentMenu({
  disabled = false,
  isRecording,
  onScreenshot,
  onScreenRecord,
  onStopRecording,
  onPhotos,
  onFiles,
  onOneDrive,
  onClipboard,
}: AttachmentMenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const actions: Record<string, () => void> = {
    onScreenshot,
    onScreenRecord,
    onPhotos,
    onFiles,
    onOneDrive,
    onClipboard,
  };

  const handleClickOutside = useCallback((event: MouseEvent) => {
    if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
      setIsOpen(false);
    }
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [handleClickOutside, isOpen]);

  const handleItemClick = (actionKey: string) => {
    setIsOpen(false);
    actions[actionKey]?.();
  };

  if (isRecording) {
    return (
      <button
        type="button"
        onClick={onStopRecording}
        className={cn(ui.button.base, 'min-h-[44px] min-w-[44px] rounded-xl bg-red-600 px-3 py-2 text-white shadow-lg shadow-red-600/30 hover:bg-red-500')}
        title="Stop recording"
        aria-label="Stop screen recording"
      >
        <span className="relative flex h-3 w-3">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-white opacity-75" />
          <span className="relative inline-flex h-3 w-3 rounded-full bg-white" />
        </span>
        <span className="whitespace-nowrap text-xs font-semibold">Recording</span>
        <Square className="h-4 w-4 fill-current" />
      </button>
    );
  }

  return (
    <div ref={menuRef} className="relative">
      <button
        type="button"
        onClick={() => setIsOpen((open) => !open)}
        disabled={disabled}
        className={cn(
          ui.button.icon,
          'min-h-[44px] min-w-[44px] rounded-xl',
          isOpen && 'rotate-45 bg-background text-foreground',
        )}
        title="Attach file"
        aria-label="Open attachment options"
        aria-haspopup="menu"
        aria-expanded={isOpen}
      >
        <Plus className="h-5 w-5 transition-transform" />
      </button>

      {isOpen ? (
        <div
          className={cn(
            'absolute bottom-full left-0 z-50 mb-2 min-w-[220px] rounded-[1.25rem] border border-border bg-card py-2 shadow-2xl shadow-black/30',
            'animate-in fade-in slide-in-from-bottom-2 duration-150',
          )}
          role="menu"
          aria-label="Attachment options"
        >
          {MENU_ITEMS.map(({ id, icon: Icon, label, action, bg, color }) => (
            <button
              key={id}
              type="button"
              onClick={() => handleItemClick(action)}
              className="flex min-h-[48px] w-full items-center gap-3 px-3 py-2 text-sm text-foreground transition-colors duration-150 hover:bg-background"
              role="menuitem"
              aria-label={label}
            >
              <div className={cn('flex h-10 w-10 items-center justify-center rounded-full', bg)}>
                <Icon className={cn('h-5 w-5', color)} />
              </div>
              <span className="font-medium">{label}</span>
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

export default AttachmentMenu;
